import { motion } from 'framer-motion';
import { useState, type CSSProperties } from 'react';
import { Sprite } from './Sprite';

interface Props {
  cracked: boolean;
  payoffEmoji: string;
  /** Beat id → its payoff art at public/comic/<beatId>.png. */
  beatId: string;
  /** Words cracked so far / total — a visible competence meter (no readable chrome). */
  done: number;
  total: number;
  /** The current mission's briefing title (Phase 6 narrative skin), if any. */
  missionTitle?: string;
  /** The current chapter's villain (the one "scrambling" — Phase 7). Defaults to Goblin. */
  villainId?: string;
  villainEmoji?: string;
}

/** The payoff scene that springs to life on a correct decode: the generated pixel-art
 *  object (public/comic/<beatId>.png), falling back to the beat's emoji if none exists. */
function PayoffArt({ beatId, emoji }: { beatId: string; emoji: string }) {
  const [failed, setFailed] = useState(false);
  if (failed) return <span style={emojiScene}>{emoji}</span>;
  return (
    <img
      src={`${import.meta.env.BASE_URL}comic/${beatId}.png`}
      width={48}
      height={48}
      alt=""
      draggable={false}
      onError={() => setFailed(true)}
      style={{ imageRendering: 'pixelated' }}
    />
  );
}

/** The villain who lurks while the panel is locked: the animated Goblin sprite for
 *  Chapter 1, else the chapter villain's pixel-art portrait (emoji fallback). */
function VillainLurker({ villainId, emoji }: { villainId: string; emoji: string }) {
  const [failed, setFailed] = useState(false);
  if (villainId === 'goblin') return <Sprite heroId="green-goblin" kind="idle" size={46} fps={5} />;
  if (failed) return <span style={emojiScene}>{emoji}</span>;
  return (
    <img
      src={`${import.meta.env.BASE_URL}villains/${villainId}.png`}
      width={46}
      height={46}
      alt=""
      draggable={false}
      onError={() => setFailed(true)}
      style={{ imageRendering: 'pixelated' }}
    />
  );
}

/** The comic strip up top: the chapter villain lurks while it's locked; the scene springs
 *  open (payoff art) on a correct decode. */
export function ComicPanel({ cracked, payoffEmoji, beatId, done, total, missionTitle, villainId = 'goblin', villainEmoji = '🦹' }: Props) {
  return (
    <div style={band}>
      <div style={leftGroup}>
        {missionTitle && <span style={missionLabel}>🎯 {missionTitle}</span>}
        <div style={panel}>
          <span style={lock}>{cracked ? '🔓' : '🔒'}</span>
          {cracked ? (
            <motion.span
              key={beatId}
              initial={{ scale: 0, rotate: -15 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: 'spring', stiffness: 320, damping: 14 }}
              style={scene}
            >
              <PayoffArt beatId={beatId} emoji={payoffEmoji} />
            </motion.span>
          ) : (
            <VillainLurker villainId={villainId} emoji={villainEmoji} />
          )}
        </div>
      </div>
      <div style={dots} aria-label={`${done} of ${total} cracked`}>
        {Array.from({ length: total }).map((_, i) => (
          <span key={i} style={{ ...dot, ...(i < done ? dotDone : null) }} />
        ))}
      </div>
    </div>
  );
}

const band: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 16,
  padding: '10px 20px',
  background: 'var(--frame-2)',
  borderBottom: '3px solid var(--frame-edge)',
};
const leftGroup: CSSProperties = { display: 'flex', alignItems: 'center', gap: 14 };
const missionLabel: CSSProperties = { fontSize: 15, fontWeight: 800, color: 'var(--gold)', maxWidth: 200 };
const panel: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 14,
  padding: '4px 18px',
  borderRadius: 14,
  background: 'var(--frame)',
  border: '2px solid var(--frame-edge)',
  minHeight: 58,
};
const lock: CSSProperties = { fontSize: 30 };
const scene: CSSProperties = { display: 'inline-flex', alignItems: 'center', lineHeight: 1 };
const emojiScene: CSSProperties = { fontSize: 36, lineHeight: 1 };
const dots: CSSProperties = { display: 'flex', gap: 8 };
const dot: CSSProperties = { width: 14, height: 14, borderRadius: '50%', background: 'var(--frame-edge)' };
const dotDone: CSSProperties = { background: 'var(--gold)' };
